<div class="json-comparison-container">
    <!-- Header with controls -->
    <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="mb-0">{{ 'JsonComparison' | localize }}</h6>
        <div class="btn-group btn-group-sm">
            <button [class]="'btn ' + (displayMode === 'all' ? 'btn-primary' : 'btn-outline-secondary')"
                    (click)="showAll()" [title]="'ShowAll' | localize">
                {{ 'All' | localize }}
            </button>
            <button [class]="'btn ' + (displayMode === 'changed' ? 'btn-primary' : 'btn-outline-secondary')"
                    (click)="showChangedOnly()" [title]="'ShowChangedOnly' | localize">
                {{ 'Changed' | localize }}
            </button>
            <button class="btn btn-outline-secondary" (click)="collapseAll()" [title]="'CollapseAll' | localize">
                {{ 'Collapse' | localize }}
            </button>
            <button class="btn btn-outline-secondary" (click)="expandAll()" [title]="'ExpandAll' | localize">
                {{ 'Expand' | localize }}
            </button>
            <button [class]="'btn ' + (showRawJson ? 'btn-primary' : 'btn-outline-secondary')"
                    (click)="toggleRaw()" [title]="'ShowRawJson' | localize">
                {{ 'Raw' | localize }}
            </button>
        </div>
    </div>

    <!-- Raw JSON view -->
    @if (showRawJson) {
        <div class="raw-json-container">
            @if (originalJson) {
                <div class="mb-3">
                    <h6 class="text-muted mb-2">{{ 'OriginalValue' | localize }}</h6>
                    <pre class="raw-json-pre bg-light p-3 rounded border" style="overflow: auto; white-space: pre-wrap; word-break: break-all;">{{ originalJson }}</pre>
                </div>
            }
            @if (newJson) {
                <div class="mb-3">
                    <h6 class="text-muted mb-2">{{ 'NewValue' | localize }}</h6>
                    <pre class="raw-json-pre bg-light p-3 rounded border" style="overflow: auto; white-space: pre-wrap; word-break: break-all;">{{ newJson }}</pre>
                </div>
            }
        </div>
    }

    <!-- Show "identical" message when both JSONs exist but have no differences -->
    @else if (showComparison && originalJson && newJson && isIdentical) {
        <div class="text-center text-success py-4">
            <i class="fa fa-check-circle fa-2x mb-2"></i>
            <div>{{ 'NoDifferencesFound' | localize }}</div>
            <small class="text-muted">{{ 'BothValuesAreIdentical' | localize }}</small>
        </div>
    }

    <!-- Show comparison if both JSONs exist OR single JSON with comparison enabled -->
    @else if ((showComparison && originalJson && newJson && comparisonTree.length > 0) || (showComparison && (originalJson || newJson) && comparisonTree.length > 0)) {
        <div class="json-tree-container">
            <div class="json-tree">
                @for (node of comparisonTree; track node.fullPath) {
                    <div class="json-node"
                         [ngClass]="'change-' + node.changeType"
                         [style.padding-left]="(node.level * 20) + 'px'">

                        <!-- Node toggle and name -->
                        <div class="node-header d-flex align-items-center">
                            @if (selectionEnabled && isSelectable(node)) {
                                @let selState = getNodeSelectionState(node);
                                <input type="checkbox"
                                       class="form-check-input me-2 json-line-checkbox"
                                       [checked]="selState === 'all'"
                                       [indeterminate]="selState === 'some'"
                                       (click)="toggleSelection(node, $event)"
                                       [attr.aria-label]="'IncludeThisChangeInImport' | localize"
                                       [title]="'IncludeThisChangeInImport' | localize" />
                            }

                            @if (node.hasChildren) {
                                <button class="btn btn-link btn-sm p-0 me-2 toggle-btn"
                                        (click)="toggleNode(node)">
                                    <i [class]="node.isExpanded ? 'fa fa-minus' : 'fa fa-plus'"></i>
                                </button>
                            } @else {
                                <span class="toggle-spacer me-2"></span>
                            }

                            <i class="fa me-2 type-icon" [ngClass]="getTypeIcon(node.type)"></i>
                            <span class="node-name">{{ node.key | localize }}</span>

                            @if (node.changeType !== 'unchanged') {
                                <span class="change-indicator ms-2">
                                    @switch (node.changeType) {
                                        @case ('added') {
                                            <span class="badge bg-success">{{ 'Added' | localize }}</span>
                                        }
                                        @case ('removed') {
                                            <span class="badge bg-danger">{{ 'Removed' | localize }}</span>
                                        }
                                        @case ('modified') {
                                            <span class="badge bg-warning">{{ 'Modified' | localize }}</span>
                                        }
                                    }

                                    <!-- Show Original/New on same line after badge - but not for objects/arrays -->
                                    @if ((node.value !== undefined || node.originalValue !== undefined || node.newValue !== undefined) && node.type !== 'object' && node.type !== 'array') {
                                        <span class="value-comparison-inline ms-3">
                                            <span class="value-text-inline-changed">{{ getOriginalValueForDisplay(node) }}</span>
                                            <span class="arrow ms-1 me-1">→</span>
                                            <span class="value-text-inline-changed">{{ getNewValueForDisplay(node) }}</span>
                                        </span>
                                    }

                                    <!-- Copy button: only in copyable mode, only on changed leaf nodes -->
                                    @if (copyable && !node.hasChildren && node.changeType !== 'added') {
                                        <button class="btn btn-link btn-sm p-0 ms-2 copy-field-btn"
                                                type="button"
                                                [title]="'CopyOriginalValue' | localize"
                                                (click)="onCopyField(node.fullPath, node.originalValue !== undefined ? node.originalValue : node.value); $event.stopPropagation()">
                                            <i class="fa fa-copy"></i>
                                        </button>
                                    }
                                </span>
                            }
                        </div>

                        <!-- Node value for unchanged elements -->
                        @if (node.changeType === 'unchanged' && node.value !== undefined && !node.hasChildren) {
                            <div class="node-value ms-4">
                                <span class="value-text">{{ formatValue(node.value) }}</span>
                            </div>
                        }

                        <!-- Hide type info for objects and arrays - no need to show Object{x} or Array[x] -->
                        <!-- @if (node.changeType === 'unchanged' && (node.type === 'object' || node.type === 'array')) {
                            <div class="node-value ms-4">
                                <span class="value-text type-info">{{ node.value }}</span>
                            </div>
                        } -->

                        <!-- Children are handled by the flattened tree structure -->
                    </div>
                }
            </div>
        </div>
    }

    <!-- Show single JSON tree if only one exists -->
    @else if (newJson && newTree.length > 0) {
        <div class="json-tree-container">
            <h6 class="text-muted mb-2">{{ 'JsonStructure' | localize }}</h6>
            <div class="json-tree">
                @for (node of newTree; track node.fullPath) {
                    <div class="json-node"
                         [style.padding-left]="(node.level * 20) + 'px'">

                        <div class="node-header d-flex align-items-center">
                            @if (node.hasChildren) {
                                <button class="btn btn-link btn-sm p-0 me-2 toggle-btn"
                                        (click)="toggleNode(node)">
                                    <i [class]="node.isExpanded ? 'fa fa-minus' : 'fa fa-plus'"></i>
                                </button>
                            } @else {
                                <span class="toggle-spacer me-2"></span>
                            }

                            <i class="fa me-2 type-icon" [ngClass]="getTypeIcon(node.type)"></i>
                            <span class="node-name">{{ node.key | localize }}</span>
                        </div>

                        @if (node.value !== undefined && !node.hasChildren) {
                            <div class="node-value ms-4">
                                <span class="value-text">{{ formatValue(node.value) }}</span>
                            </div>
                        }

                        <!-- Hide type info for objects and arrays - no need to show Object{x} or Array[x] -->
                        <!-- @if ((node.type === 'object' || node.type === 'array')) {
                            <div class="node-value ms-4">
                                <span class="value-text type-info">{{ node.value }}</span>
                            </div>
                        } -->
                    </div>
                }
            </div>
        </div>
    }

    <!-- No data message -->
    @else {
        <div class="text-center text-muted py-4">
            <i class="fa fa-code fa-2x mb-2"></i>
            <div>{{ 'NoJsonData' | localize }}</div>
        </div>
    }
</div>